import Tooltip from '@/components/tooltip/tooltip-component';
import { recursiveFlatMap } from '@/pages/company-management/company-products/utils';
import { AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { MdPlaylistAddCheck } from 'react-icons/md';
import DropdownComponent from './config-components/dropdown.component';
import Folder from './config-components/folder.component';
import OptionFolder from './config-components/option-folder.component';
import Option from './config-components/option.component';
import { useProductOptions } from './config-context';
import Styled from './configuration-style';

const recursiveUnselectChildren = (node) => {
  const retVal = { ...node, id: null };
  if (node.children) {
    retVal.children = retVal.children.map((child) =>
      recursiveUnselectChildren(child),
    );
  }
  return retVal;
};

/**
 * RenderOption component to render different types of options
 *
 * @param {object} props - The props object
 * @param {object} props.node - The node object
 * @param {string} props.name - The name of the option
 * @param {number} [props.level] - The nesting level
 * @param {object} props.parentNode - The parent node object
 * @param {string} props.parentName - The name of the parent node
 * @param {Array} [props.initialValues] - Array of initial values
 * @param {boolean} [props.isFirst] - Indicates if it is the first folder being rendered
 * @param {string} props.parentOptionFolderName - Indicates if it is the parent option being rendered
 * @param {string} props.partNumber - Part Number of option
 * @param {number} props.companyId - Company ID
 * @param {object} props.expandedParentOption - The expanded parent option
 * @param {Function} props.setExpandedParentOption - The function to set the expanded parent option
 * @param {boolean} props.isTemplate - Indicates if it is a template option
 * @returns {JSX.Element|null} The rendered option component
 */
const RenderOption = ({
  node,
  companyId,
  name,
  level,
  parentNode,
  parentName,
  initialValues,
  isFirst,
  parentOptionFolderName,
  partNumber,
  expandedParentOption,
  setExpandedParentOption,
  isTemplate,
}) => {
  const [initialValueSet, setInitialValueSet] = useState(false);
  const { control, setValue, getValues } = useFormContext();
  const {
    fetchUEImagesFunc,
    showDependencies,
    setShowDependencies,
    productOptions,
    templateOptions,
  } = useProductOptions();
  const [infoModal, setInfoModal] = useState({
    show: null,
    node: null,
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const isDropdown =
    parentNode.select_type === 'dropdown' || node.select_type === 'dropdown';
  const isParentRequired = parentNode.is_required;
  let type = node.option_type;
  const hasChildren = node?.children?.length > 0;
  if (hasChildren && type === 'option') {
    type = 'option_folder';
  }
  if (isDropdown) {
    type = 'dropdown';
  }

  useEffect(() => {
    if (!initialValueSet) {
      const value = initialValues?.find(
        (x) => x.product_to_option_link_id === node.id,
      );
      if (
        value &&
        getValues(`${name}.id`) !== value.product_to_option_link_id
      ) {
        if (value.below_the_line) {
          setValue(`${name}.belowTheLine`, value.below_the_line);
        } else {
          setValue(`${name}.id`, value.product_to_option_link_id);
        }
      }
      setInitialValueSet(true);
    }
  }, [initialValues, node.id, name, setValue, getValues, initialValueSet]);

  const renderRequiredAsterisk = useCallback(() => {
    if (getValues(name)?.is_required || getValues(parentName)?.is_required) {
      return <Styled.Required>*</Styled.Required>;
    }
    return null;
  }, [getValues, name, parentName]);

  const nestedResetValue = useCallback((option) => {
    const retVal = { ...option, id: null, dependencies: null };
    if (option.children) {
      retVal.children = option.children.map((element) =>
        nestedResetValue(element),
      );
    }
    return retVal;
  }, []);

  const validateOption = useCallback(
    ({ parentNode: pNode, parentName: pName }) => {
      if (pNode?.option_type === 'folder' && parentOptionFolderName) {
        if (pNode?.is_required) {
          let validation = '*';
          pNode?.children.forEach((_, cIndex) => {
            const childName = `${pName}.${cIndex + 1}`;
            const childValue = getValues(childName);
            if (validation === '*') {
              if (childValue?.id) {
                validation = true;
              }
            }
          });
          if (getValues(parentOptionFolderName)?.id) {
            validation = true;
          }
          return validation;
        }
        return true;
      }

      if (pNode?.is_required) {
        if (pNode?.option_type === 'folder') {
          let validation = '*';
          pNode?.children.forEach((_, cIndex) => {
            const childName = `${pName}.${cIndex + 1}`;
            const childValue = getValues(childName);
            if (validation === '*') {
              if (childValue?.id) {
                validation = true;
              }
            }
          });
          return validation;
        }
        const value = getValues(pName);
        if (value?.children) {
          const selectedOptions = value.children.filter((option) => option.id);
          if (selectedOptions.length > 0) {
            return true;
          }
          return '*';
        }
      }
      return true;
    },
    [getValues, parentOptionFolderName],
  );

  const renderBelowTheLine = () => (
    <Controller
      render={({ field }) => (
        <Styled.OverTheLineIconContainer
          onClick={(e) => {
            e.stopPropagation();
            setValue(field.name, !field.value);
          }}
          isBelowTheLine={field.value}
        >
          <Tooltip
            icon={<MdPlaylistAddCheck size={20} />}
            id={`${node.id}_belowTheLine`}
            text={field.value ? '+ Remove Below The Line' : '+ Below The Line'}
          />
        </Styled.OverTheLineIconContainer>
      )}
      name={`${name}.belowTheLine`}
      control={control}
    />
  );

  const handleOptionClick = ({ onChange, value }) => {
    // If this isn't a Template, use productOptions, otherwise use templateOptions
    const flatOptions = !isTemplate
      ? recursiveFlatMap(productOptions, [])
      : recursiveFlatMap(templateOptions, []);
    // let showDep = false;
    // #region DependencyCheck
    // // Check if we need to show the dependency modal
    // if (
    //   !value &&
    //   node?.dependencies?.length > 0 &&
    //   node?.dependencies?.some((dep) =>
    //     flatOptions.find((q) => q.option_price_link_id === dep),
    //   )
    // ) {
    //   showDep = true;
    //   // check if there is elements for dependencies
    //   setShowDependencies({
    //     type: 'SET_SHOW_DEPENDENCIES',
    //     payload: { show: false, node },
    //   });
    // }
    // // #endregion DependencyCheck

    // #region OptionCheck
    // First we check if the option is already selected
    if (value) {
      // If the option is already selected, we need to uncheck any and all children.
      // We do not care what select type it is. All children should be unchecked.
      // We need to check if the parent option is required. If it is we cannot unsellect the current one.
      const parentOptionNode = flatOptions.find((q) => q.id === node.parent_id);
      const isFormOptionMulti = parentOptionNode?.select_type === 'multi';
      if (isParentRequired && !isFormOptionMulti) {
        return;
      }
      if (isParentRequired && isFormOptionMulti) {
        // We need to check if there is more than one option selected. If there is we can unselect the current one.
        const parent = parentName.split('.');
        parent.pop();
        const parentNameString = parent.join('.');
        const parentValue = getValues(parentNameString);
        if (
          parentValue.children.filter((q) => q.id !== node.id && q.id)
            .length === 0
        ) {
          return;
        }
      }
      const formValues = getValues(name);
      const newValues = recursiveUnselectChildren(formValues);
      setValue(name, newValues, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    } else if (node.parent_id) {
      onChange(value ? null : node.id);
      const handleParentCheck = (pName, parentId, nodeId) => {
        const parentOptionNode = flatOptions.find((q) => q.id === parentId);
        const isFormOptionMulti = parentOptionNode?.select_type === 'multi';
        const parent = pName.split('.');
        parent.pop();
        const parentNameString = parent.join('.');
        const parentValue = getValues(parentNameString);
        if (isFormOptionMulti) {
          // Here we just need to check if the parent is not selected. If it is not we need to select it.
          if (!parentValue.id) {
            parentValue.children = parentValue.children.map((child) => {
              const retVal = child;
              if (nodeId === child.node || nodeId === child.id) {
                retVal.id = child.node;
              }
              return retVal;
            });
            parentValue.id = parentValue.node ?? parentOptionNode.id;
            setValue(parentNameString, parentValue, {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true,
            });
          }
        } else {
          parentValue.children = parentValue.children.map((child) => {
            let retVal = child;
            if (nodeId === child.node || nodeId === child.id) {
              retVal.id = child.node;
            } else {
              retVal = recursiveUnselectChildren(retVal);
            }
            return retVal;
          });
          parentValue.id = parentValue?.node ?? parentOptionNode?.id;
          setValue(parentNameString, parentValue, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          });
        }
        if (parentOptionNode?.parent_id) {
          const grandParentName = parentNameString.split('.');
          grandParentName.pop();
          const grandParentNameString = grandParentName.join('.');
          handleParentCheck(
            grandParentNameString,
            parentOptionNode.parent_id,
            parentOptionNode.id,
          );
        }
      };

      handleParentCheck(parentName, node.parent_id, node.id);
    } else {
      onChange(value ? null : node.id);
    }

    if (fetchUEImagesFunc || value) {
      fetchUEImagesFunc();
    }
    // #endregion OptionCheck
  };

  const renderChildren = (parOptionFolderName, shouldWrap = true) => (
    <AnimatePresence>
      <Styled.Drawer
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <Styled.StyledChildrenContainer
          isExpanded={
            !parentOptionFolderName
              ? expandedParentOption && isExpanded
              : isExpanded
          }
          level={level * 10}
          shouldWrap={shouldWrap}
        >
          <>
            {node.children
              .filter((child) => !child?.is_hidden)
              .map((childOption, index) => (
                <RenderOption
                  key={childOption.id}
                  companyId={companyId}
                  node={childOption}
                  name={`${name}.children.${index}`}
                  level={level + 1}
                  parentName={`${name}.children`}
                  parentNode={node}
                  initialValues={initialValues}
                  parentOptionFolderName={parOptionFolderName}
                  partNumber={`${partNumber}_${childOption.option_type === 'folder' ? childOption.name : childOption.part_number}`}
                  isTemplate={isTemplate}
                />
              ))}
          </>
        </Styled.StyledChildrenContainer>
      </Styled.Drawer>
    </AnimatePresence>
  );

  if (showDependencies.show) {
    nestedResetValue(node);
  }

  switch (type) {
    case 'folder': {
      return (
        <Folder
          node={node}
          name={name}
          isExpanded={
            !parentOptionFolderName
              ? expandedParentOption && isExpanded
              : isExpanded
          }
          setIsExpanded={setIsExpanded}
          setExpandedParentOption={setExpandedParentOption}
          level={level}
          isFirst={isFirst}
          renderChildren={renderChildren}
          isRequired={renderRequiredAsterisk}
          parentOptionFolderName={parentOptionFolderName}
          partNumber={partNumber}
        />
      );
    }
    case 'dropdown': {
      return (
        <DropdownComponent
          name={name}
          parentName={parentName}
          parentNode={parentNode}
          label={node.name}
          level={level}
          options={node.children.filter((child) => !child?.is_hidden)}
          onSelect={(onChange, value) => {
            handleOptionClick({
              onChange,
              value,
            });
          }}
          validateOption={validateOption}
        />
      );
    }
    case 'option_folder': {
      return (
        <OptionFolder
          node={node}
          name={name}
          parentName={parentName}
          parentNode={parentNode}
          setExpandedParentOption={setExpandedParentOption}
          isExpanded={
            !parentOptionFolderName
              ? expandedParentOption && isExpanded
              : isExpanded
          }
          setIsExpanded={setIsExpanded}
          level={level}
          isFirst={isFirst}
          renderChildren={renderChildren}
          hasChildren={hasChildren}
          handleOptionClick={handleOptionClick}
          nestedResetValue={nestedResetValue}
          validateOption={validateOption}
          isRequired={renderRequiredAsterisk}
          infoModal={infoModal}
          renderBelowTheLine={renderBelowTheLine}
          setInfoModal={setInfoModal}
          partNumber={partNumber}
        />
      );
    }
    case 'option': {
      return (
        <Option
          node={node}
          name={name}
          companyId={companyId}
          parentNode={parentNode}
          parentName={parentName}
          validateOption={validateOption}
          renderBelowTheLine={renderBelowTheLine}
          infoModal={infoModal}
          setInfoModal={setInfoModal}
          handleOptionClick={handleOptionClick}
          partNumber={partNumber}
        />
      );
    }
    default: {
      return null;
    }
  }
};

RenderOption.propTypes = {
  node: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  level: PropTypes.number,
  parentNode: PropTypes.object.isRequired,
  parentName: PropTypes.string.isRequired,
  parentOptionFolderName: PropTypes.string.isRequired,
  initialValues: PropTypes.array,
  isFirst: PropTypes.bool,
  partNumber: PropTypes.string.isRequired,
  companyId: PropTypes.string.isRequired,
  expandedParentOption: PropTypes.bool,
  setExpandedParentOption: PropTypes.func,
  isTemplate: PropTypes.bool,
};

RenderOption.defaultProps = {
  level: 0,
  initialValues: [],
  isFirst: false,
  expandedParentOption: null,
  setExpandedParentOption: () => {},
  isTemplate: false,
};

export default RenderOption;
